// src/components/NotesWindow.js
import React, { useEffect, useRef, useState } from "react";
import { X, StickyNote, Send, GripHorizontal } from "lucide-react";
import { api } from "../scripts/api";

export default function NotesWindow({
  open,
  inventoryId,
  inventoryTitle,
  onClose,
  onSeen, // callback to refresh badge summary
}) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const STORAGE_KEY = `ims_notes_window_pos`;
  const dragRef = useRef({ dragging: false, sx: 0, sy: 0, ox: 0, oy: 0 });

  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) return saved;
    } catch {}
    return { x: Math.max(20, window.innerWidth - 520), y: 90 };
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch {}
  }, [pos]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.dragging) return;
      const dx = e.clientX - dragRef.current.sx;
      const dy = e.clientY - dragRef.current.sy;

      const w = 480;
      const h = 520;

      const nx = dragRef.current.ox + dx;
      const ny = dragRef.current.oy + dy;

      setPos({
        x: Math.min(Math.max(10, nx), Math.max(10, window.innerWidth - w - 10)),
        y: Math.min(Math.max(10, ny), Math.max(10, window.innerHeight - h - 10)),
      });
    };

    const onUp = () => (dragRef.current.dragging = false);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const markSeen = async () => {
    if (!inventoryId) return;
    try {
      await api.post(`/inventory/${inventoryId}/notes/seen`);
      onSeen?.();
    } catch {
      // ignore, don't block UI
    }
  };

  const loadNotes = async () => {
    if (!inventoryId) return;
    setBusy(true);
    setStatus("");
    try {
      const { data } = await api.get(`/inventory/${inventoryId}/notes`);
      setNotes(Array.isArray(data?.notes) ? data.notes : []);
      await markSeen();
    } catch (e) {
      setStatus(e?.response?.data?.error || e?.message || "Failed to load notes");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (open && inventoryId) loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inventoryId]);

  const onHeaderMouseDown = (e) => {
    if (e.button !== 0) return;
    dragRef.current.dragging = true;
    dragRef.current.sx = e.clientX;
    dragRef.current.sy = e.clientY;
    dragRef.current.ox = pos.x;
    dragRef.current.oy = pos.y;
    e.preventDefault();
  };

  const addNote = async () => {
    const t = String(body || "").trim();
    if (!t || !inventoryId) return;

    setBusy(true);
    setStatus("");
    try {
      const { data } = await api.post(`/inventory/${inventoryId}/notes`, { body: t });
      if (data?.ok && data?.note) {
        setNotes((prev) => [...prev, data.note]);
        setBody("");
        await markSeen();
      } else {
        setStatus("Note add failed");
      }
    } catch (e) {
      setStatus(e?.response?.data?.error || e?.message || "Note add failed");
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      addNote();
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: 480,
        height: 520,
        zIndex: 2200,
        borderRadius: 14,
        background: "var(--card-bg)",
        border: "1px solid var(--card-border)",
        boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* draggable header */}
      <div
        onMouseDown={onHeaderMouseDown}
        style={{
          cursor: "grab",
          userSelect: "none",
          padding: "10px 12px",
          borderBottom: "1px solid var(--card-border)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "rgba(0,0,0,0.02)",
        }}
      >
        <GripHorizontal size={16} className="muted" />
        <StickyNote size={18} />
        <div style={{ fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          Notes{inventoryTitle ? ` • ${inventoryTitle}` : ""}
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn-outline" type="button" onClick={onClose} title="Close">
          <X size={16} />
        </button>
      </div>

      {/* list */}
      <div style={{ padding: 12, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div className="muted" style={{ fontSize: 12 }}>
            {busy ? "Loading…" : `${notes.length} note(s)`} • Ctrl/⌘+Enter to add
          </div>
          <button className="btn-outline" type="button" onClick={loadNotes} disabled={busy}>
            Refresh
          </button>
        </div>

        {status ? (
          <div className="muted" style={{ fontSize: 12 }}>
            {status}
          </div>
        ) : null}

        <div className="card" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          {notes.length === 0 ? (
            <div className="muted" style={{ fontSize: 12 }}>
              No notes yet.
            </div>
          ) : (
            notes.map((n) => (
              <div key={n.id} style={{ padding: "10px 6px", borderBottom: "1px solid var(--card-border)" }}>
                <div className="muted" style={{ fontSize: 12, opacity: 0.9 }}>
                  {(n.user_label || "user") + " • " + new Date(n.created_at).toLocaleString()}
                </div>
                <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{n.body}</div>
              </div>
            ))
          )}
        </div>

        {/* composer */}
        <textarea
          className="input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Add a note…"
          rows={3}
          style={{ resize: "none" }}
        />

        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button
            className="btn"
            type="button"
            onClick={addNote}
            disabled={busy || !String(body || "").trim()}
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Send size={16} />
            Add Note
          </button>
        </div>
      </div>
    </div>
  );
}